const OPENROUTER_ENDPOINT = 'https://openrouter.ai/api/v1/chat/completions';
const DEFAULT_OPENROUTER_MODEL = 'openai/gpt-4o-mini';

const SYSTEM_PROMPT = [
  'You are a concise map assistant for discovering events and places.',
  'Use the provided map context when relevant.',
  'If information is missing, say so clearly and suggest a next query.',
  'Keep answers practical and short.'
].join(' ');

const normalizeChatMessages = (messages) => {
  if (!Array.isArray(messages)) {
    return [];
  }

  return messages
    .filter((entry) => (
      entry &&
      (entry.role === 'user' || entry.role === 'assistant') &&
      typeof entry.content === 'string' &&
      entry.content.trim().length > 0
    ))
    .slice(-12)
    .map((entry) => ({
      role: entry.role,
      content: entry.content.trim().slice(0, 3000)
    }));
};

const parseAssistantContent = (content) => {
  if (typeof content === 'string') {
    return content.trim();
  }

  if (Array.isArray(content)) {
    const text = content
      .map((part) => {
        if (typeof part === 'string') {
          return part;
        }

        if (part && typeof part.text === 'string') {
          return part.text;
        }

        return '';
      })
      .join('\n')
      .trim();
    return text;
  }

  return '';
};

export const sendMapChatMessage = async ({ messages, contextSummary, signal }) => {
  const apiKey = process.env.REACT_APP_OPENROUTER_API_KEY;
  if (!apiKey) {
    throw new Error('Missing REACT_APP_OPENROUTER_API_KEY.');
  }

  const model = process.env.REACT_APP_OPENROUTER_MODEL || DEFAULT_OPENROUTER_MODEL;
  const safeMessages = normalizeChatMessages(messages);
  const mapContext = (contextSummary && contextSummary.trim().length > 0
    ? '\n\nMap context:\n' + contextSummary.trim()
    : '');

  const payload = {
    model: model,
    temperature: 0.35,
    max_tokens: 320,
    messages: [
      { role: 'system', content: SYSTEM_PROMPT + mapContext },
      ...safeMessages
    ]
  };

  const response = await fetch(OPENROUTER_ENDPOINT, {
    method: 'POST',
    signal: signal,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + apiKey,
      'HTTP-Referer': (typeof window !== 'undefined' ? window.location.origin : 'http://localhost'),
      'X-Title': 'Strangers Map Assistant'
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    let errorDetails = '';
    try {
      const errorPayload = await response.json();
      errorDetails = (errorPayload && errorPayload.error && errorPayload.error.message ? errorPayload.error.message : '');
    } catch (error) {
      errorDetails = '';
    }
    const suffix = (errorDetails ? ' ' + errorDetails : '');
    throw new Error('OpenRouter request failed (' + response.status + ').' + suffix);
  }

  const data = await response.json();
  const choice = (data && Array.isArray(data.choices) ? data.choices[0] : null);
  const content = (choice && choice.message ? parseAssistantContent(choice.message.content) : '');
  if (!content) {
    throw new Error('OpenRouter returned an empty response.');
  }

  return content;
};
